import { useMemo, useState } from 'react';
import { genreList, movieList } from '@/mocks/movies';

export default function Catalog() {
  const [genre, setGenre] = useState('全部');
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return movieList.filter((m) => {
      const matchGenre = genre === '全部' || m.genre === genre;
      const matchKw =
        kw.length === 0 ||
        m.title.toLowerCase().includes(kw) ||
        m.region.toLowerCase().includes(kw) ||
        m.genre.toLowerCase().includes(kw) ||
        m.tagline.toLowerCase().includes(kw);
      return matchGenre && matchKw;
    });
  }, [genre, keyword]);

  return (
    <section id="catalog" className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-accent-600 text-xs md:text-sm font-medium tracking-wide">
              <i className="ri-movie-line" />
              精选片单
            </span>
            <h2 className="mt-3 font-heading font-bold text-2xl md:text-4xl text-foreground-950 text-balance">
              按类型筛选，快速找到想看的
            </h2>
            <p className="mt-4 text-foreground-700 text-sm md:text-base leading-relaxed">
              下面用一组示例片单演示本站的信息组织方式：你可以点击类型标签筛选，也可以在搜索框输入片名、
              地区或关键词，列表会即时更新。片单与评分为演示内容，仅用于说明浏览方式。
            </p>
          </div>

          <div className="w-full lg:w-72 shrink-0">
            <label htmlFor="movie-search" className="sr-only">
              搜索片名、地区或关键词
            </label>
            <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-md bg-background-100 border border-background-300/70 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-foreground-500 text-base" />
              <input
                id="movie-search"
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索片名 / 地区 / 关键词"
                className="w-full bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-500"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword('')}
                  aria-label="清空搜索"
                  className="text-foreground-500 hover:text-foreground-800 transition-colors"
                >
                  <i className="ri-close-circle-line" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {genreList.map((g) => {
            const active = genre === g;
            return (
              <button
                key={g}
                type="button"
                onClick={() => setGenre(g)}
                className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                  active
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-700 border border-background-200/80 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                {g}
              </button>
            );
          })}
        </div>

        <p className="mt-5 text-xs md:text-sm text-foreground-600">
          共找到 <span className="text-accent-600 font-semibold">{filtered.length}</span> 条示例片单
          {genre !== '全部' && <span> · 类型：{genre}</span>}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {filtered.map((m) => (
              <article
                key={m.id}
                className="group rounded-lg overflow-hidden bg-background-100/70 border border-background-200/80 hover:border-primary-300 transition-colors"
              >
                <div className="relative w-full aspect-[2/3] bg-background-200">
                  <img
                    src={m.poster}
                    alt={`示例片单《${m.title}》的海报插画`}
                    className="w-full h-full object-top object-cover"
                  />
                  <span className="absolute top-2 left-2 px-2 py-1 rounded-md bg-background-50/90 text-[11px] font-medium text-accent-700">
                    {m.genre}
                  </span>
                  <span className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-foreground-950/80 text-[11px] font-medium text-background-50">
                    {m.rating.toFixed(1)}
                  </span>
                </div>
                <div className="p-3.5 md:p-4">
                  <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 truncate">
                    {m.title}
                  </h3>
                  <p className="mt-1.5 text-[11px] md:text-xs text-foreground-500">
                    {m.region} · {m.year} · {m.duration}
                  </p>
                  <p className="mt-2.5 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-3">
                    {m.tagline}
                  </p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100/60 py-14 flex flex-col items-center text-center px-4">
            <i className="ri-inbox-archive-line text-4xl text-foreground-400" />
            <p className="mt-3 text-sm text-foreground-700">没有找到符合条件的片单</p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setGenre('全部');
              }}
              className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}